<template>
    <Teleport to="body">
        <div v-if="award" class="fso-overlay" role="dialog" aria-modal="true" aria-labelledby="sgaOverlayTitle">
            <!--<div class="fso-backdrop" @click="close" />-->
            <div class="fso-backdrop" />

            <div class="fso-card" :class="{ 'fso-card--has-image': theme.image }" :style="cssVars">
                <div class="fso-orb fso-orb--1" aria-hidden="true" />
                <div class="fso-orb fso-orb--2" aria-hidden="true" />

                <!--<button class="fso-close" aria-label="Close" @click="close">&times;</button>-->

                <!-- Themed hero image (mothers-day) -->
                <div v-if="theme.image" class="fso-image-wrap">
                    <img :src="theme.image" alt="" class="fso-image" aria-hidden="true">
                    <div class="fso-image-overlay" />
                    <div class="fso-decorations" aria-hidden="true">
                        <span v-for="(deco, i) in theme.decorations" :key="i" class="fso-deco" :class="`fso-deco--${i + 1}`" v-html="deco" />
                    </div>
                </div>

                <!-- Emoji ring (themes with `decorations[]` and no image, e.g. Valentine's) -->
                <div
                    v-else-if="decorationStyle === 'emoji-ring'"
                    class="fso-decorations fso-decorations--inline"
                    aria-hidden="true"
                >
                    <span
                        v-for="(deco, i) in theme.decorations"
                        :key="i"
                        class="fso-deco"
                        :class="`fso-deco--${i + 1}`"
                        v-html="deco"
                    />
                </div>

                <!-- Procedural star field (default theme, anniversary, …) -->
                <div v-else class="fso-decorations fso-decorations--inline" aria-hidden="true">
                    <div class="fso-stars-aura" aria-hidden="true" />

                    <span class="fso-star fso-star--hero" aria-hidden="true">
                        <img :src="STAR_IMG" alt="">
                    </span>

                    <span
                        v-for="s in starField"
                        :key="s.id"
                        class="fso-star"
                        :class="`fso-star--${s.anim}`"
                        :style="{
                            left: s.x + '%',
                            top: s.y + '%',
                            width: s.size + 'px',
                            height: s.size + 'px',
                            '--star-base-opacity': s.opacity,
                            filter: s.blur > 0 ? `blur(${s.blur}px)` : null,
                            animationDelay: s.delay + 's',
                            animationDuration: s.dur + 's',
                        }"
                    >
                        <img :src="STAR_IMG" alt="">
                    </span>

                    <div
                        v-for="(sh, i) in SHOOTING_STARS"
                        :key="`sh-${i}`"
                        class="fso-shooting"
                        :class="`fso-shooting--${i + 1}`"
                        :style="{ top: sh.top + '%' }"
                        aria-hidden="true"
                    >
                        <div
                            class="fso-shooting__streak"
                            :style="{ animationDelay: sh.delay + 's', animationDuration: sh.dur + 's' }"
                        />
                    </div>
                </div>

                <div class="fso-body">
                    <div class="fso-badge fso-anim-in" style="animation-delay:0.15s">{{ theme.badge }}</div>

                    <h2 id="sgaOverlayTitle" class="fso-title fso-anim-in" style="animation-delay:0.25s">
                        {{ theme.title }}
                    </h2>

                    <div class="fso-amount fso-anim-in" style="animation-delay:0.35s">
                        <img :src="STAR_IMG" alt="Star" class="fso-amount-star">
                        <span class="fso-amount-number">{{ award.stars.toLocaleString() }}</span>
                        <span class="fso-amount-label">Stars</span>
                    </div>

                    <p class="fso-text fso-anim-in" style="animation-delay:0.42s">{{ dynamicText }}</p>

                    <!-- Refined CTA layout — applies to all themes; retinted per theme via --fso-accent-* vars. -->
                    <div class="fso-actions fso-actions--refined fso-anim-in" style="animation-delay:0.52s">
                        <a :href="theme.cta_url" class="fso-btn fso-btn--primary-na" @click="track('Campaign Overlay Browse Auditions Button Clicked')">
                            {{ theme.cta }} <span class="fso-btn-arrow" aria-hidden="true">→</span>
                        </a>
                        <a :href="theme.cta2_url" class="fso-btn fso-btn--ghost-na" @click="track('Campaign Overlay Create Pitch Button Clicked')">
                            {{ theme.cta2 }} <span class="fso-btn-arrow" aria-hidden="true">→</span>
                        </a>

                        <div v-if="showUpgradeUpsell || showStarsUpsell" class="fso-upsells">
                            <div class="fso-upsells__eyebrow">{{ upsellEyebrow }}</div>

                            <a
                                v-if="showUpgradeUpsell"
                                :href="upgradeUpsell.url"
                                class="fso-upsell-row"
                                :class="`fso-upsell-row--${upgradeUpsell.variant}`"
                                @click="track(upgradeUpsell.event)"
                            >
                                <span
                                    class="fso-upsell-row__icon"
                                    :class="`fso-upsell-row__icon--${upgradeUpsell.variant}`"
                                    aria-hidden="true"
                                >
                                    <!-- Crown icon for Gold tier -->
                                    <svg
                                        v-if="upgradeUpsell.variant === 'gold'"
                                        class="fso-upsell-row__svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" />
                                        <path d="M5 21h14" />
                                    </svg>
                                    <!-- Star glyph for account upgrade (trial/expired) -->
                                    <span v-else>★</span>
                                </span>
                                <span class="fso-upsell-row__copy">
                                    <span class="fso-upsell-row__title">
                                        {{ upgradeUpsell.title }}
                                        <span class="fso-upsell-row__chip">{{ upgradeUpsell.chip }}</span>
                                    </span>
                                    <span class="fso-upsell-row__sub">{{ upgradeUpsell.sub }}</span>
                                </span>
                                <span class="fso-upsell-row__chev" aria-hidden="true">→</span>
                            </a>

                            <button
                                v-if="showStarsUpsell"
                                class="fso-upsell-row"
                                @click="handleBuyStarPack"
                            >
                                <span class="fso-upsell-row__icon fso-upsell-row__icon--star" aria-hidden="true">
                                    <img :src="STAR_IMG" alt="">
                                </span>
                                <span class="fso-upsell-row__copy">
                                    <span class="fso-upsell-row__title">{{ theme.cta_stars }}</span>
                                    <span class="fso-upsell-row__sub">Top up instantly</span>
                                </span>
                                <span class="fso-upsell-row__chev" aria-hidden="true">→</span>
                            </button>
                        </div>

                        <button class="fso-text-dismiss" @click="close('maybe-later')">Maybe later</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue'

const STAR_IMG = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    award: {
        type: Object,
        default: null, // { theme, stars, goal_stars, campaign_bonus_stars, goal_label, campaign_completed, autocamp }
    },
    auth: {type: Object},
})

const emit = defineEmits(['close', 'buyStarPack'])

const THEMES = {
    default: {
        badge:        'Gift Claimed',
        title:        "You've got stars — use them.",
        text:         'Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.',
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  [], // → star-field decoration mode
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
            '--fso-border':       'rgba(59,130,246,0.35)',
            '--fso-shadow-color': 'rgba(59,130,246,0.18)',
            '--fso-badge-bg':     'linear-gradient(135deg,#3b82f6,#2563eb)',
            '--fso-title-color':  '#f1f5f9',
            '--fso-amount-color': '#60a5fa',
            '--fso-text-color':   '#94a3b8',
            '--fso-btn-bg':       '#3b82f6',
            '--fso-btn-hover':    '#2563eb',
            '--fso-close-color':  '#64748b',
            '--fso-deco-size':    '28px',
            '--fso-glow':         'rgba(59,130,246,0.5)',
            // Accent palette — drives refined CTA primary/ghost buttons, the
            // account-variant upsell row, and the star-field glow colors.
            '--fso-accent-1':          '#3b82f6',
            '--fso-accent-2':          '#2563eb',
            '--fso-accent-bright':     '#60a5fa',
            '--fso-accent-rgb':        '59, 130, 246',
            '--fso-accent-bright-rgb': '96, 165, 250',
        },
    },

    'mothers-day': {
        badge:        'Welcome Back 🌷',
        title:        "You've got stars — use them.",
        text:         "As a thank-you for coming back, we've loaded your account with complimentary stars. Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        'https://aws-lg.naughtycdn.com/mediaAssets/100514/ma_modal.png',
        decorations:  ['🌸', '🌹', '💐', '🌺', '🌷'], // overlay on hero image
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #1c0a14 0%, #2e1222 55%, #1c0a14 100%)',
            '--fso-border':       'rgba(236,72,153,0.4)',
            '--fso-shadow-color': 'rgba(236,72,153,0.2)',
            '--fso-badge-bg':     'linear-gradient(135deg,#e879a0,#be185d)',
            '--fso-title-color':  '#fce7f3',
            '--fso-amount-color': '#f9a8d4',
            '--fso-text-color':   '#e4aec5',
            '--fso-btn-bg':       '#be185d',
            '--fso-btn-hover':    '#9d174d',
            '--fso-close-color':  '#9d6b7f',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(236,72,153,0.6)',
            '--fso-accent-1':          '#be185d',
            '--fso-accent-2':          '#831843',
            '--fso-accent-bright':     '#f9a8d4',
            '--fso-accent-rgb':        '190, 24, 93',
            '--fso-accent-bright-rgb': '249, 168, 212',
        },
    },

    'valentines-day': {
        badge:        'With Love 💌',
        title:        'Stars for the lover in you.',
        text:         "Hand-picked stars on us — spend them on the auditions that move you, or pitch a fantasy worth filming this season.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  ['❤️', '💕', '💝', '🌹', '💌'], // → emoji-ring mode
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #1a0a0e 0%, #2e1217 55%, #1a0a0e 100%)',
            '--fso-border':       'rgba(225,29,72,0.4)',
            '--fso-shadow-color': 'rgba(225,29,72,0.22)',
            '--fso-badge-bg':     'linear-gradient(135deg, #fb7185, #e11d48)',
            '--fso-title-color':  '#ffe4e6',
            '--fso-amount-color': '#fb7185',
            '--fso-text-color':   '#e4aec5',
            '--fso-btn-bg':       '#e11d48',
            '--fso-btn-hover':    '#9f1239',
            '--fso-close-color':  '#9f6b76',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(225,29,72,0.6)',
            '--fso-accent-1':          '#e11d48',
            '--fso-accent-2':          '#9f1239',
            '--fso-accent-bright':     '#fb7185',
            '--fso-accent-rgb':        '225, 29, 72',
            '--fso-accent-bright-rgb': '251, 113, 133',
        },
    },

    anniversary: {
        badge:        'Anniversary Drop 🎉',
        title:        'A year together, on us.',
        text:         "We loaded your account with stars to mark the occasion. Back an audition, pitch your own scene, or save them for what's next.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  [], // → star-field decoration mode (retinted violet)
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0f0a1c 0%, #1c1138 55%, #0f0a1c 100%)',
            '--fso-border':       'rgba(139,92,246,0.4)',
            '--fso-shadow-color': 'rgba(139,92,246,0.22)',
            '--fso-badge-bg':     'linear-gradient(135deg, #a78bfa, #7c3aed)',
            '--fso-title-color':  '#f1ecff',
            '--fso-amount-color': '#c4b5fd',
            '--fso-text-color':   '#b8aed2',
            '--fso-btn-bg':       '#7c3aed',
            '--fso-btn-hover':    '#6d28d9',
            '--fso-close-color':  '#695b8a',
            '--fso-deco-size':    '28px',
            '--fso-glow':         'rgba(139,92,246,0.6)',
            '--fso-accent-1':          '#7c3aed',
            '--fso-accent-2':          '#4c1d95',
            '--fso-accent-bright':     '#c4b5fd',
            '--fso-accent-rgb':        '139, 92, 246',
            '--fso-accent-bright-rgb': '196, 181, 253',
        },
    },
}

const theme = computed(() => THEMES[props.award?.theme] ?? THEMES.default)

const cssVars = computed(() =>
    Object.entries(theme.value.css).map(([k, v]) => `${k}:${v}`).join(';')
)

// ── Decoration mode resolver ────────────────────────────────────────────
// Themes pick a decoration treatment based on what their config ships:
//   - 'hero-image' : has `image` URL → full-bleed hero with emoji overlay
//   - 'emoji-ring' : no image, `decorations[]` populated → 5-position emoji ring
//   - 'star-field' : neither → procedural particle field (retinted per theme)
const decorationStyle = computed(() => {
    if (theme.value.image) return 'hero-image'
    if (theme.value.decorations && theme.value.decorations.length > 0) return 'emoji-ring'
    return 'star-field'
})

// ── Membership-aware upsell logic ────────────────────────────────────────
const isGold    = computed(() => props.auth?.is_gold)
const isExpired = computed(() => !props.auth?.has_reels_subscription)
const isTrial = computed(() => props.auth?.is_trial)

const showStarsUpsell = computed(() => true) // star packs stay relevant at every tier

const upgradeVariant = computed(() => {
    if (isTrial.value || isExpired.value) return 'account'
    if (!isGold.value) return 'gold'
    return null
})
const showUpgradeUpsell = computed(() => upgradeVariant.value !== null)

const upgradeUpsell = computed(() => {
    if (isExpired.value) {
        return {
            variant: 'account',
            title: 'Upgrade your account',
            sub:   'Pick up where you left off — reactivate now',
            chip:  'EXPIRED',
            url:   route('members2.account.upgrade'),
            event: 'Campaign Overlay Expired Upgrade Button Clicked',
        }
    }
    if (isTrial.value) {
        return {
            variant: 'account',
            title: 'Upgrade your account',
            sub:   'Your trial ends soon — keep your perks',
            chip:  'TRIAL',
            url:   route('members2.account.upgrade'),
            event: 'Campaign Overlay Trial Upgrade Button Clicked',
        }
    }

    // 'gold' — paying monthly/yearly user without Gold
    return {
        variant: 'gold',
        title: 'Upgrade to',
        sub:   'Monthly star drops',
        chip:  'GOLD',
        url:   route('members2.account.naGold', {source: 'sga_overlay'}),
        event: 'Campaign Overlay Upgrade to Gold Button Clicked',
    }
})

const upsellEyebrow = computed(() => {
    if (!showUpgradeUpsell.value) return 'Stock up on stars'
    if (isExpired.value)          return 'Welcome back — pick up where you left off'
    if (isTrial.value)            return 'Trial ending — keep your perks'
    return 'Want to go further?'
})

const dynamicText = computed(() => {
    if (!props.award) return theme.value.text

    const { goal_stars, campaign_bonus_stars, goal_label, campaign_completed } = props.award

    if (goal_stars > 0 && goal_label) {
        let message = `You've got ${goal_stars.toLocaleString()} ${goal_stars === 1 ? 'star' : 'stars'} for ${goal_label}`

        if (campaign_completed && campaign_bonus_stars > 0) {
            message += ` and ${campaign_bonus_stars.toLocaleString()} extra ${campaign_bonus_stars === 1 ? 'star' : 'stars'} for completing the campaign`
        }

        message += '!'
        return message
    }

    return theme.value.text
})

// ── Procedural star field (default-theme decorations) ────────────────────
const starField = computed(() => {
    const N = 22
    const out = []
    for (let i = 0; i < N; i++) {
        const s = i * 13.37 + 7
        out.push({
            id: i,
            x: (s * 17) % 100,                  // 0–100%
            y: (s * 29) % 100,
            size: 5 + ((s * 11) % 18),          // 5–23 px
            opacity: 0.25 + ((s * 13) % 75) / 100,
            blur: ((s * 19) % 22) / 10,         // 0–2.2 px
            delay: -((s * 23) % 40) / 10,       // negative → pre-rolled loop
            dur: 2.6 + ((s * 31) % 38) / 10,    // 2.6–6.4 s
            anim: ['twinkle', 'drift', 'slowspin'][i % 3],
        })
    }
    return out
})

const SHOOTING_STARS = [
    { top: 22, delay: 1.2, dur: 2.4 },
    { top: 64, delay: 4.8, dur: 2.1 },
]

watch(() => props.award, (val) => {
    if (val) track('Campaign Overlay Displayed')
})

function track(event) {
    if (typeof LTIAnalytics !== 'undefined') {
        LTIAnalytics.track(event, {
            autocamp: props.award?.autocamp,
            is_trial: isTrial.value,
            is_expired: isExpired.value,
            is_gold: isGold.value,
        })
    }
}

function close(reason = 'close') {
    if (reason === 'maybe-later') track('Campaign Overlay Maybe Later Button Clicked')
    else track('Campaign Overlay Close Button Clicked')
    emit('close')
}

// Close on Escape
// if (typeof window !== 'undefined') {
//     window.addEventListener('keydown', (e) => {
//         if (e.key === 'Escape' && props.award) close()
//     })
// }

const handleBuyStarPack = () => {
    track('Campaign Overlay Buy Star Packs Button Clicked')
    emit('buyStarPack')
}
</script>

<style scoped>
.fso-overlay {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.fso-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(6px);
    cursor: pointer;
    animation: fso-fade-in 0.3s ease both;
}

.fso-card {
    position: relative;
    z-index: 1;
    background: var(--fso-card-bg);
    border: 1px solid var(--fso-border);
    border-radius: 22px;
    max-width: 460px;
    width: 100%;
    text-align: center;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.7), 0 0 60px var(--fso-shadow-color);
    animation: fso-card-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.fso-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(50px);
    opacity: 0.18;
    animation: fso-orb-drift 8s ease-in-out infinite alternate;
}
.fso-orb--1 { width: 220px; height: 220px; background: var(--fso-glow); top: -60px; left: -60px; animation-duration: 9s; }
.fso-orb--2 { width: 180px; height: 180px; background: var(--fso-glow); bottom: -50px; right: -40px; animation-duration: 11s; animation-direction: alternate-reverse; }

.fso-close {
    position: absolute;
    top: 12px;
    right: 14px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.35);
    border: none;
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 9px;
    border-radius: 50%;
    backdrop-filter: blur(4px);
    transition: background 0.2s, transform 0.2s;
}
.fso-close:hover { background: rgba(0, 0, 0, 0.6); transform: scale(1.1); }

.fso-image-wrap { position: relative; width: 100%; height: 180px; overflow: hidden; }
.fso-image { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; animation: fso-img-zoom 12s ease-in-out infinite alternate; }
.fso-image-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.9) 100%); }

.fso-decorations { position: absolute; inset: 0; pointer-events: none; }
.fso-decorations--inline {
    position: relative;
    height: 110px;
    margin-bottom: 6px;
    overflow: hidden;
    /* Soft-fade the bottom edge so stars/aura/shooting-streaks dissolve into
       the card bg instead of being sliced on a hard horizontal line. */
    -webkit-mask-image: linear-gradient(to bottom,
    black 0%,
    black 55%,
    rgba(0,0,0,0.6) 80%,
    transparent 100%);
    mask-image: linear-gradient(to bottom,
    black 0%,
    black 55%,
    rgba(0,0,0,0.6) 80%,
    transparent 100%);
}

/* Legacy emoji decorations — still used by mothers-day. */
.fso-deco {
    position: absolute;
    font-size: var(--fso-deco-size, 26px);
    opacity: 0;
    line-height: 1;
    animation: fso-deco-in 0.6s ease forwards, fso-float 4s ease-in-out infinite;
}
.fso-deco--1 { top: 18px; left: 18%;  animation-delay: 0.1s, 0.7s; }
.fso-deco--2 { top: 6px;  left: 50%;  margin-left: -18px; font-size: calc(var(--fso-deco-size, 26px) + 12px); animation-delay: 0.02s, 0.6s; }
.fso-deco--3 { top: 18px; right: 18%; animation-delay: 0.16s, 0.8s; }
.fso-deco--4 { top: 40px; left: 8%;   font-size: calc(var(--fso-deco-size, 26px) - 8px); animation-delay: 0.24s, 1.0s; }
.fso-deco--5 { top: 40px; right: 8%;  font-size: calc(var(--fso-deco-size, 26px) - 8px); animation-delay: 0.24s, 1.2s; }

/* ── Procedural star field — themed via --fso-accent-* ─────────────────────
   Aura, star glow, hero glow and shooting-star colors all derive from the
   theme's accent palette (set on the card via :style="cssVars"), so the
   default (blue), anniversary (violet) and any future colorways re-tint
   automatically without touching the field itself. */
.fso-stars-aura {
    position: absolute;
    inset: -10% 5% 0;
    background:
        radial-gradient(55% 70% at 50% 55%, rgba(var(--fso-accent-rgb), 0.22), transparent 70%),
        radial-gradient(35% 50% at 22% 30%, rgba(var(--fso-accent-bright-rgb), 0.14), transparent 70%),
        radial-gradient(30% 45% at 78% 35%, rgba(var(--fso-accent-bright-rgb), 0.10), transparent 70%);
    filter: blur(8px);
    pointer-events: none;
    animation: fso-aura-breathe 6s ease-in-out infinite;
}

.fso-star {
    position: absolute;
    display: block;
    pointer-events: none;
    margin: 0;
    line-height: 0;
    translate: -50% -50%;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
    animation-fill-mode: both;
    opacity: var(--star-base-opacity, 0.6);
}
.fso-star img {
    width: 100%; height: 100%;
    display: block;
    filter: drop-shadow(0 0 5px rgba(var(--fso-accent-bright-rgb), 0.7));
}

.fso-star--hero {
    top: 50%; left: 50%;
    width: 44px; height: 44px;
    z-index: 3;
    animation:
        fso-hero-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both,
        fso-hero-spin 7s ease-in-out 0.7s infinite,
        fso-hero-pulse 3.2s ease-in-out 0.7s infinite;
}
.fso-star--hero img {
    filter:
        drop-shadow(0 0 14px rgba(var(--fso-accent-bright-rgb), 0.85))
        drop-shadow(0 0 28px rgba(var(--fso-accent-rgb), 0.55));
}

.fso-star--twinkle  { animation-name: fso-twinkle; }
.fso-star--drift    { animation-name: fso-drift; }
.fso-star--slowspin { animation-name: fso-slowspin; animation-timing-function: linear; }

/* Shooting streaks — rotated container with a sliding gradient inside. */
.fso-shooting {
    position: absolute;
    left: -25%;
    width: 150%;
    height: 1px;
    pointer-events: none;
}
.fso-shooting--1 { rotate: 18deg; }
.fso-shooting--2 { rotate: 24deg; }
.fso-shooting__streak {
    position: relative;
    width: 80px;
    height: 1px;
    background: linear-gradient(to right,
    transparent,
    rgba(var(--fso-accent-bright-rgb), 0.7) 60%,
    #E0EDFF 100%);
    filter: drop-shadow(0 0 4px rgba(var(--fso-accent-bright-rgb), 0.7));
    opacity: 0;
    animation-name: fso-shoot;
    animation-iteration-count: infinite;
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.45, 0.95);
}
.fso-shooting__streak::after {
    content: '';
    position: absolute;
    right: -2px; top: -2px;
    width: 5px; height: 5px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 0 8px rgba(var(--fso-accent-bright-rgb), 1), 0 0 16px rgba(var(--fso-accent-bright-rgb), 0.5);
}

.fso-body { padding: 28px 36px 32px; }
.fso-card--has-image .fso-body { padding-top: 22px; }

.fso-badge { display: inline-block; padding: 5px 14px; background: var(--fso-badge-bg); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; border-radius: 20px; margin-bottom: 14px; }
.fso-title { font-size: 22px; font-weight: 700; color: var(--fso-title-color); margin: 0 0 16px; line-height: 1.3; }
.fso-text  { color: var(--fso-text-color); font-size: 13px; line-height: 1.7; margin: 0 0 24px; }

.fso-amount { display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.fso-amount-star   { width: 28px; height: 28px; animation: fso-star-spin 6s linear infinite; filter: drop-shadow(0 0 6px var(--fso-glow)); }
.fso-amount-number { font-size: 32px; font-weight: 800; color: var(--fso-amount-color); line-height: 1; text-shadow: 0 0 20px var(--fso-glow); animation: fso-amount-pulse 3s ease-in-out infinite; }
.fso-amount-label  { font-size: 16px; font-weight: 600; color: var(--fso-amount-color); opacity: 0.8; align-self: flex-end; padding-bottom: 3px; }

.fso-actions { display: flex; flex-direction: column; gap: 10px; }
.fso-actions--refined { gap: 8px; }
.fso-ctas { display: flex; gap: 10px; }
.fso-ctas .fso-btn { flex: 1; }
.fso-ctas--featured { margin-bottom: 2px; }

.fso-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 12px 16px; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer; text-decoration: none; transition: background 0.2s, transform 0.18s, box-shadow 0.2s, color 0.2s; }

/* ── Refined CTA buttons — themed via --fso-accent-* ──────────────────────── */
.fso-btn--primary-na {
    background: linear-gradient(135deg, var(--fso-accent-1), var(--fso-accent-2));
    color: #fff; border: none;
    width: 100%;
    padding: 14px 18px;
    font-size: 14px;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(var(--fso-accent-rgb), 0.35), 0 0 0 1px rgba(var(--fso-accent-bright-rgb), 0.18) inset;
    letter-spacing: 0.01em;
}
.fso-btn--primary-na:hover {
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(var(--fso-accent-rgb), 0.5), 0 0 0 1px rgba(var(--fso-accent-bright-rgb), 0.3) inset;
}
.fso-btn--ghost-na {
    background: rgba(var(--fso-accent-bright-rgb), 0.06);
    color: var(--fso-accent-bright);
    border: 1px solid rgba(var(--fso-accent-rgb), 0.4);
    width: 100%;
    padding: 12px 18px;
    font-size: 13px;
    border-radius: 12px;
}
.fso-btn--ghost-na:hover {
    background: rgba(var(--fso-accent-bright-rgb), 0.10);
    border-color: rgba(var(--fso-accent-bright-rgb), 0.6);
    color: #fff;
    transform: translateY(-2px);
}
.fso-btn-arrow { display: inline-block; transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
.fso-btn--primary-na:hover .fso-btn-arrow,
.fso-btn--ghost-na:hover .fso-btn-arrow { transform: translateX(4px); }

/* ── Original CTA buttons (mothers-day path) ─────────────────────────────── */
.fso-btn--gold {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #1c1917;
    border: none;
    box-shadow: 0 4px 18px rgba(245, 158, 11, 0.35);
    font-weight: 800;
    letter-spacing: 0.4px;
}
.fso-btn--gold:hover {
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    color: #1c1917;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(245, 158, 11, 0.5);
}
.fso-btn--stars { background: var(--fso-btn-bg); color: #fff; border: none; box-shadow: 0 4px 16px var(--fso-shadow-color); }
.fso-btn--stars:hover { background: var(--fso-btn-hover); color: #fff; transform: translateY(-2px); box-shadow: 0 8px 24px var(--fso-shadow-color); }

/* Account-variant button (mothers-day path, trial/expired) — NA brand blue */
.fso-btn--account {
    background: linear-gradient(135deg, #0052CC, #2A318D);
    color: #fff;
    border: none;
    box-shadow: 0 4px 18px rgba(0,82,204,0.35);
    font-weight: 800;
    letter-spacing: 0.4px;
}
.fso-btn--account:hover {
    background: linear-gradient(135deg, #4D94FF, #0052CC);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,82,204,0.5);
}
.fso-btn--sm { padding: 9px 12px; font-size: 11px; font-weight: 600; opacity: 0.85; }
.fso-btn--sm:hover { opacity: 1; }
.fso-btn--outline { background: transparent; color: var(--fso-amount-color); border: 1px solid var(--fso-border); }
.fso-btn--outline:hover { background: rgba(255,255,255,0.05); border-color: var(--fso-amount-color); transform: translateY(-2px); }
.fso-btn--secondary { background: transparent; color: var(--fso-close-color); border: 1px solid rgba(255,255,255,0.1); font-weight: 500; font-size: 12px; }
.fso-btn--secondary:hover { color: #aaa; border-color: rgba(255,255,255,0.22); }

/* ── Upsell rows ───────────────────────────────────────────────────────────
   TODO: The gold accent values (#FBBF24, #D97706, #FDE68A, etc.) are
         intentionally hardcoded — "Gold" is a brand-fixed signal, not a
         theme variable. If you ever need to retheme the gold accent (e.g.
         a seasonal red/green campaign), promote these to
         --fso-upsell-gold-bg / --fso-upsell-gold-border / etc. */
.fso-upsells {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(255,255,255,0.08);
    display: flex; flex-direction: column; gap: 8px;
}
.fso-upsells__eyebrow {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin-bottom: 4px;
}

.fso-upsell-row {
    display: flex; align-items: center; gap: 12px;
    text-align: left; text-decoration: none;
    padding: 11px 14px;
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.18s;
    position: relative;
    overflow: hidden;
}
.fso-upsell-row:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.18); transform: translateY(-1px); }
.fso-upsell-row:hover .fso-upsell-row__chev { transform: translateX(3px); opacity: 1; }

.fso-upsell-row__icon {
    flex: 0 0 auto;
    width: 36px; height: 36px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 10px;
    font-size: 18px;
    line-height: 1;
}
.fso-upsell-row__icon--gold {
    background: linear-gradient(135deg, #FBBF24, #D97706);
    color: #1c1917;
    font-weight: 900;
    box-shadow: 0 4px 14px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.3);
}
/* Account-variant icon tile (trial / expired) — themed accent */
.fso-upsell-row__icon--account {
    background: linear-gradient(135deg, var(--fso-accent-bright), var(--fso-accent-1));
    color: #fff;
    font-weight: 900;
    box-shadow: 0 4px 14px rgba(var(--fso-accent-rgb), 0.35), inset 0 1px 0 rgba(255,255,255,0.25);
}
.fso-upsell-row__svg {
    width: 20px;
    height: 20px;
    display: block;
}
.fso-upsell-row__icon--star {
    background: rgba(var(--fso-accent-rgb), 0.18);
    border: 1px solid rgba(var(--fso-accent-bright-rgb), 0.25);
}
.fso-upsell-row__icon--star img {
    width: 22px; height: 22px;
    filter: drop-shadow(0 0 6px rgba(var(--fso-accent-bright-rgb), 0.6));
}

.fso-upsell-row__copy { flex: 1 1 auto; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.fso-upsell-row__title { display: inline-flex; align-items: center; gap: 8px; color: #fff; font-size: 13.5px; font-weight: 700; letter-spacing: 0.01em; }
.fso-upsell-row__chip {
    display: inline-block;
    padding: 2px 6px;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.1em;
    border-radius: 4px;
    line-height: 1;
}
.fso-upsell-row--gold .fso-upsell-row__chip {
    background: linear-gradient(135deg, #FBBF24, #D97706);
    color: #1c1917;
}
.fso-upsell-row--account .fso-upsell-row__chip {
    background: linear-gradient(135deg, var(--fso-accent-bright), var(--fso-accent-1));
    color: #fff;
}
.fso-upsell-row__sub { color: rgba(255,255,255,0.55); font-size: 11.5px; font-weight: 500; line-height: 1.35; }
.fso-upsell-row__chev {
    flex: 0 0 auto;
    color: rgba(255,255,255,0.4);
    font-size: 16px;
    font-weight: 700;
    opacity: 0.7;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s;
}

.fso-upsell-row--gold {
    background: linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(251,191,36,0.02) 50%, rgba(255,255,255,0.025) 100%);
    border-color: rgba(251,191,36,0.22);
}
.fso-upsell-row--gold:hover {
    background: linear-gradient(135deg, rgba(251,191,36,0.14) 0%, rgba(251,191,36,0.04) 50%, rgba(255,255,255,0.05) 100%);
    border-color: rgba(251,191,36,0.4);
    box-shadow: 0 6px 20px rgba(251,191,36,0.12);
}
.fso-upsell-row--gold .fso-upsell-row__sub { color: rgba(253,230,138,0.7); }

/* Account-variant upsell row (trial / expired) — themed accent */
.fso-upsell-row--account {
    background: linear-gradient(135deg, rgba(var(--fso-accent-rgb), 0.10) 0%, rgba(var(--fso-accent-rgb), 0.03) 50%, rgba(255,255,255,0.025) 100%);
    border-color: rgba(var(--fso-accent-rgb), 0.28);
}
.fso-upsell-row--account:hover {
    background: linear-gradient(135deg, rgba(var(--fso-accent-rgb), 0.18) 0%, rgba(var(--fso-accent-rgb), 0.05) 50%, rgba(255,255,255,0.05) 100%);
    border-color: rgba(var(--fso-accent-bright-rgb), 0.5);
    box-shadow: 0 6px 20px rgba(var(--fso-accent-rgb), 0.18);
}
.fso-upsell-row--account .fso-upsell-row__sub { color: rgba(var(--fso-accent-bright-rgb), 0.75); }

.fso-text-dismiss {
    background: none; border: none;
    color: var(--fso-close-color);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px;
    margin-top: 2px;
    transition: color 0.2s;
}
.fso-text-dismiss:hover { color: rgba(255,255,255,0.7); }

.fso-anim-in { opacity: 0; animation: fso-content-in 0.5s ease forwards; }

@keyframes fso-fade-in     { from { opacity: 0; } to { opacity: 1; } }
@keyframes fso-card-in     { from { opacity: 0; transform: translateY(32px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes fso-orb-drift   { from { transform: translate(0, 0) scale(1); } to { transform: translate(30px, 20px) scale(1.15); } }
@keyframes fso-img-zoom    { from { transform: scale(1); } to { transform: scale(1.06); } }
@keyframes fso-deco-in     { from { opacity: 0; transform: scale(0.2) translateY(14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes fso-float       { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
@keyframes fso-star-spin   { 0%, 100% { transform: rotate(-8deg) scale(1); } 50% { transform: rotate(8deg) scale(1.15); } }
@keyframes fso-amount-pulse{ 0%, 100% { text-shadow: 0 0 16px var(--fso-glow); } 50% { text-shadow: 0 0 32px var(--fso-glow); } }
@keyframes fso-content-in  { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

@keyframes fso-twinkle {
    0%, 100% { opacity: calc(var(--star-base-opacity, 0.6) * 0.25); scale: 0.85; }
    50%      { opacity: var(--star-base-opacity, 0.6);              scale: 1.25; }
}
@keyframes fso-drift {
    0%   { translate: -50% -50%; opacity: calc(var(--star-base-opacity, 0.6) * 0.5); }
    25%  { translate: -45% -57%; opacity: var(--star-base-opacity, 0.6); }
    50%  { translate: -53% -60%; opacity: calc(var(--star-base-opacity, 0.6) * 0.7); }
    75%  { translate: -57% -52%; opacity: var(--star-base-opacity, 0.6); }
    100% { translate: -50% -50%; opacity: calc(var(--star-base-opacity, 0.6) * 0.5); }
}
@keyframes fso-slowspin {
    from { rotate: 0deg;   opacity: calc(var(--star-base-opacity, 0.6) * 0.6); }
    50%  {                 opacity: var(--star-base-opacity, 0.6); }
    to   { rotate: 360deg; opacity: calc(var(--star-base-opacity, 0.6) * 0.6); }
}
@keyframes fso-hero-pop {
    0%   { translate: -50% -50%; scale: 0; opacity: 0; rotate: -90deg; }
    60%  { scale: 1.25; opacity: 1; rotate: 8deg; }
    100% { translate: -50% -50%; scale: 1; opacity: 1; rotate: 0deg; }
}
@keyframes fso-hero-spin {
    0%, 100% { rotate: -10deg; scale: 1; }
    50%      { rotate:  10deg; scale: 1.08; }
}
@keyframes fso-hero-pulse {
    0%, 100% { filter: drop-shadow(0 0 12px rgba(var(--fso-accent-bright-rgb), 0.7)) drop-shadow(0 0 24px rgba(var(--fso-accent-rgb), 0.35)); }
    50%      { filter: drop-shadow(0 0 22px rgba(var(--fso-accent-bright-rgb), 1)) drop-shadow(0 0 44px rgba(var(--fso-accent-rgb), 0.7)); }
}
@keyframes fso-aura-breathe {
    0%, 100% { opacity: 0.7; scale: 1; }
    50%      { opacity: 1;   scale: 1.08; }
}
@keyframes fso-shoot {
    0%        { transform: translateX(-80px); opacity: 0; }
    8%        { opacity: 1; }
    45%       { opacity: 1; }
    65%, 100% { transform: translateX(560px); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
    .fso-star, .fso-stars-aura, .fso-shooting__streak { animation: none !important; }
    .fso-star { opacity: var(--star-base-opacity, 0.6); }
}

@media (max-width: 480px) {
    .fso-body          { padding: 20px 20px 26px; }
    .fso-title         { font-size: 18px; }
    .fso-amount-number { font-size: 26px; }
    .fso-image-wrap    { height: 140px; }
    .fso-ctas          { flex-direction: column; }
}
</style>
